import React, { useState, useEffect } from 'react';
import { 
  Play, Brain, Target, Zap, ChevronRight, ChevronDown,
  Lightbulb, CheckCircle, Clock, Users, Star, Award,
  ArrowRight, BookOpen, TrendingUp, Eye, MessageCircle
} from 'lucide-react';
import { EnhancedAnalysis } from '../../types/enhancedAnalysis';

interface InteractiveLearningProps {
  analysis: EnhancedAnalysis;
  onStartPractice: () => void;
  onViewThinkingProcess: () => void;
}

interface ThinkingStepProps {
  step: {
    id: string;
    title: string;
    description: string;
    reasoning: string;
    outcome: string;
  };
  isActive: boolean;
  isCompleted: boolean;
  onActivate: () => void;
}

const ThinkingStep: React.FC<ThinkingStepProps> = ({ 
  step, 
  isActive, 
  isCompleted, 
  onActivate 
}) => {
  return (
    <div
      onClick={onActivate}
      className={`cursor-pointer transition-all duration-300 ${
        isActive 
          ? 'bg-blue-50 border-blue-300 shadow-md' 
          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
      } border-2 rounded-xl p-6`}
    >
      <div className="flex items-start space-x-4">
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          isCompleted 
            ? 'bg-green-500 text-white' 
            : isActive 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 text-gray-600'
        }`}>
          {isCompleted ? <CheckCircle className="w-4 h-4" /> : step.id}
        </div>
        
        <div className="flex-1">
          <h4 className={`font-semibold mb-2 ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
            {step.title}
          </h4>
          <p className="text-gray-600 text-sm mb-3">{step.description}</p>
          
          {isActive && (
            <div className="space-y-3 mt-4">
              <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                <div className="text-xs font-medium text-yellow-800 mb-1">思考过程</div>
                <div className="text-sm text-yellow-700">{step.reasoning}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <div className="text-xs font-medium text-green-800 mb-1">分析结果</div>
                <div className="text-sm text-green-700">{step.outcome}</div>
              </div>
            </div>
          )}
        </div>
        
        <ChevronRight className={`w-5 h-5 transition-transform ${
          isActive ? 'rotate-90 text-blue-500' : 'text-gray-400'
        }`} />
      </div>
    </div>
  );
};

const InteractiveLearning: React.FC<InteractiveLearningProps> = ({
  analysis,
  onStartPractice,
  onViewThinkingProcess
}) => {
  const [activeTab, setActiveTab] = useState<'thinking' | 'practice' | 'community'>('thinking');
  const [activeThinkingStep, setActiveThinkingStep] = useState<string>('1');
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [practiceProgress, setPracticeProgress] = useState(0);

  // 模拟思维过程步骤
  const thinkingSteps = [
    {
      id: '1',
      title: '问题识别',
      description: '首先识别和定义核心问题',
      reasoning: '通过分析背景信息，我们发现主要问题是...',
      outcome: '明确了需要解决的核心问题和分析目标'
    },
    {
      id: '2',
      title: '信息收集',
      description: '收集相关数据和背景信息',
      reasoning: '从多个维度收集信息，包括市场数据、用户反馈等...',
      outcome: '获得了全面的信息基础，为分析提供支撑'
    },
    {
      id: '3',
      title: '框架应用',
      description: '运用SWOT分析框架进行系统分析',
      reasoning: '选择SWOT框架是因为它能够全面评估内外部因素...',
      outcome: '完成了系统性的四维度分析'
    },
    {
      id: '4',
      title: '洞察提炼',
      description: '从分析结果中提炼关键洞察',
      reasoning: '通过对比分析和趋势判断，发现了几个关键模式...',
      outcome: '形成了具有指导意义的核心洞察'
    }
  ];

  const markStepCompleted = (stepId: string) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
    
    // 自动移动到下一步
    const currentIndex = thinkingSteps.findIndex(step => step.id === stepId);
    if (currentIndex < thinkingSteps.length - 1) {
      setActiveThinkingStep(thinkingSteps[currentIndex + 1].id);
    }
  };

  const startPracticeSession = () => {
    setPracticeProgress(25);
    onStartPractice();
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-8 border border-indigo-200">
      {/* 头部 */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4">
          <Brain className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">互动学习中心</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          深入了解分析师的思维过程，通过实践练习掌握专业分析技能
        </p>
      </div>

      {/* 标签页导航 */}
      <div className="flex justify-center mb-8">
        <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-200">
          {[
            { id: 'thinking', name: '思维过程', icon: Brain },
            { id: 'practice', name: '实践练习', icon: Target },
            { id: 'community', name: '社区讨论', icon: MessageCircle }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-indigo-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 思维过程标签页 */}
      {activeTab === 'thinking' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">分析师思维过程</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                <span>约 15 分钟学习时间</span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {thinkingSteps.map((step) => (
                <ThinkingStep
                  key={step.id}
                  step={step}
                  isActive={activeThinkingStep === step.id}
                  isCompleted={completedSteps.has(step.id)}
                  onActivate={() => setActiveThinkingStep(step.id)}
                />
              ))}
            </div>

            {/* 完成步骤按钮 */}
            <div className="flex justify-center">
              <button
                onClick={() => markStepCompleted(activeThinkingStep)}
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                理解了这一步
              </button>
            </div>
          </div>

          {/* 学习进度 */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">学习进度</h4>
              <span className="text-sm text-gray-500">
                {completedSteps.size} / {thinkingSteps.length} 步骤完成
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${(completedSteps.size / thinkingSteps.length) * 100}%` }}
              />
            </div>
            {completedSteps.size === thinkingSteps.length && (
              <div className="text-center">
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <Award className="w-4 h-4 mr-2" />
                  恭喜！您已完成思维过程学习
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 实践练习标签页 */}
      {activeTab === 'practice' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">动手实践</h3>
              <p className="text-gray-600">
                运用刚学到的分析方法，对类似案例进行实践分析
              </p>
            </div>

            {/* 练习选项 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">引导式练习</h4>
                    <p className="text-sm text-gray-600">跟随步骤完成分析</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">难度等级</span>
                    <span className="text-green-600 font-medium">入门级</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">预计时间</span>
                    <span className="text-gray-900 font-medium">20-30分钟</span>
                  </div>
                </div>
                <button
                  onClick={startPracticeSession}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Play className="w-4 h-4 mr-2" />
                  开始引导练习
                </button>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">自由分析</h4>
                    <p className="text-sm text-gray-600">独立完成案例分析</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">难度等级</span>
                    <span className="text-yellow-600 font-medium">进阶级</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">预计时间</span>
                    <span className="text-gray-900 font-medium">45-60分钟</span>
                  </div>
                </div>
                <button
                  onClick={onStartPractice}
                  className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Target className="w-4 h-4 mr-2" />
                  开始自由分析
                </button>
              </div>
            </div>

            {/* 练习进度 */}
            {practiceProgress > 0 && (
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">当前练习进度</h4>
                  <span className="text-sm text-gray-500">{practiceProgress}% 完成</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${practiceProgress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">继续完成您的分析练习</span>
                  <button
                    onClick={onStartPractice}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    继续练习 →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 社区讨论标签页 */}
      {activeTab === 'community' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">社区讨论</h3>
              <p className="text-gray-600">
                与其他学习者交流心得，分享不同的分析视角
              </p>
            </div>

            {/* 讨论统计 */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{analysis.socialMetrics.comments}</div>
                <div className="text-sm text-gray-500">讨论回复</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">156</div>
                <div className="text-sm text-gray-500">参与用户</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">4.8</div>
                <div className="text-sm text-gray-500">平均评分</div>
              </div>
            </div>

            {/* 热门讨论话题 */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">热门讨论话题</h4>
              {[
                {
                  title: '这个分析框架还可以应用到哪些场景？',
                  replies: 23,
                  likes: 45,
                  author: '分析达人'
                },
                {
                  title: '我觉得还应该考虑竞争对手的反应',
                  replies: 18,
                  likes: 32,
                  author: '商业观察者'
                },
                {
                  title: '数据来源的可靠性如何验证？',
                  replies: 15,
                  likes: 28,
                  author: '数据专家'
                }
              ].map((topic, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 mb-1">{topic.title}</h5>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>by {topic.author}</span>
                        <span>{topic.replies} 回复</span>
                        <span>{topic.likes} 点赞</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>

            {/* 参与讨论按钮 */}
            <div className="mt-6 text-center">
              <button className="inline-flex items-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors">
                <MessageCircle className="w-4 h-4 mr-2" />
                参与讨论
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 敬请期待区域 */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="text-center py-8 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border-2 border-dashed border-indigo-200">
          <div className="max-w-md mx-auto">
            <div className="w-14 h-14 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              我的分析笔记
            </h3>
            <p className="text-gray-600 mb-4 text-sm leading-relaxed">
              即将推出个人分析笔记功能，您可以记录学习心得、保存分析模板、追踪学习进度。
            </p>
            <div className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-500 text-sm">
              <Clock className="w-4 h-4 mr-2" />
              功能开发中
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveLearning;